import type { Doc } from "building-types/doc";
import { docEntityTypeToEligibleTargetedSettingsType } from "building-types/entitySettings/docEntityTypesToEligibleTargetedSettingsType";
import {
  entityTypesEligibleForEntitySettings,
  type EligibleEntitySettingEntity,
  type EntitySettings,
  type EntityTypesEligibleForEntitySettings,
} from "building-types/entitySettings/entitySettingsTypes";
import type { Group } from "building-types/groups/group";
import type {
  DocEntityHandle,
  EntitySettingsHandle,
  SettingsSetHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { EntitySettingsEligibleHandle } from "building-types/handles/entitySettingsEligibleHandle";
import type { SettingsSet } from "building-types/settings/settingsSet";
import { SettingsSetBehaviorType } from "building-types/settings/settingsSetBehaviorType";
import type { SettingsType } from "building-types/settings/settingsType";
import type { RWTable, Table } from "common/data/table";
import arrayUniqueByKey from "common/utils/arrayUniqueByKey";
import objectFilter from "common/utils/objectFilter";
import objectIsEmpty from "common/utils/objectIsEmpty";
import objectKeyFilter from "common/utils/objectKeyFilter";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import { docNormalizeEntityIdToHandles, docNormalizeHandles } from "../docNormalizeHandles";
import docEntityGetChildren from "../entity/docEntityGetChildren";
import { isEntitySettingsEligibleHandle } from "../entity/docEntityHandleTools";
import { docGetSettingSetSettings } from "../settingsSetUtils";
import { entityIsTargetedSettingsSet } from "../settingsTools";
import docGetEntity from "./docGetEntity";
import { docGetEntityHandleById } from "./docGetEntityHandleById";
import { docGetEntityId } from "./docGetEntityId";
import { docGetGroupedAndUngroupedEntities } from "./docGetGroupedAndUngroupedEntities";
import { docGetTableEntity } from "./docGetTableEntity";

/**
 * Get the eligible entity types for a given targeted settings set
 */
export function docGetEligibleEntityTypesForTargetedSettingsSet(
  doc: Doc,
  handle: SettingsSetHandle,
): Set<EntityTypesEligibleForEntitySettings> {
  const settings = docGetSettingSetSettings(doc, handle);
  const entityTypes = new Set<EntityTypesEligibleForEntitySettings>();

  for (const setting of settings) {
    const eligibleEntityTypes = objectKeyFilter(docEntityTypeToEligibleTargetedSettingsType, (key) =>
      docEntityTypeToEligibleTargetedSettingsType[key].includes(setting.type),
    );

    if (objectIsEmpty(eligibleEntityTypes)) {
      continue;
    }

    for (const entityType in eligibleEntityTypes) {
      entityTypes.add(entityType as EntityTypesEligibleForEntitySettings);
    }
  }

  return entityTypes;
}

/**
 * Get the inclusive or overlapping eligible entity types for a given set of targeted settings sets.
 * This is used to determine an eligible set of entity types to apply the settings sets to.
 */
export function docGetInclusiveEligibleEntityTypesForTargetedSettingsSets(
  doc: Doc,
  handles: SettingsSetHandle[],
): Set<EntityTypesEligibleForEntitySettings> | null {
  if (handles.length === 0) {
    return null;
  }

  const settingsSets = handles.map((handle) => docGetTableEntity(doc, handle)).filter(Boolean) as SettingsSet[];

  if (!settingsSets.every(entityIsTargetedSettingsSet)) {
    return null;
  }

  const settingsSetsSettings: RWTable<Set<EntityTypesEligibleForEntitySettings>> = {};

  for (const handle of handles) {
    settingsSetsSettings[handle.id] = docGetEligibleEntityTypesForTargetedSettingsSet(doc, handle);
  }

  // Initialize with the first set
  const commonEntityTypes = new Set(settingsSetsSettings[handles[0].id]);

  // Intersect with all other sets
  for (let i = 1; i < handles.length; i++) {
    const currentSet = settingsSetsSettings[handles[i].id];
    for (const entityType of commonEntityTypes) {
      if (!currentSet.has(entityType)) {
        commonEntityTypes.delete(entityType);
      }
    }
  }

  return commonEntityTypes;
}

/**
 * Will accumulate the shared entity settings included in doc.entitySettings that are associated with the given handles
 */

export function docGetInclusiveEntitySettingsForEntities(
  doc: Doc,
  handle: DocEntityHandle | DocEntityHandle[] | null,
  settingsType?: SettingsType,
): EntitySettings[] {
  if (objectIsEmpty(doc.entitySettings)) {
    return [];
  }

  if (!handle || (Array.isArray(handle) && !handle.length)) {
    return [];
  }

  const handles = docNormalizeHandles(doc, handle, entityTypesEligibleForEntitySettings, (entityHandle) =>
    isEntitySettingsEligibleHandle(doc, entityHandle),
  );

  if (!handles.length) {
    return [];
  }

  const entityIds = handles.map((handle) => docGetEntityId(doc, handle)).filter(Boolean) as string[];

  if (!entityIds.length) {
    return [];
  }

  const entityIdSet = new Set(entityIds);
  const entitySettings: RWTable<EntitySettings> = {};
  const uniqueSettingsOrSettingsSetIds = new Set<string>();

  for (const id in doc.entitySettings) {
    const entitySetting = doc.entitySettings[id];

    if (!entityIdSet.has(entitySetting.entityId)) {
      continue;
    }

    const targetedSettingsSet = docGetSettingsSetForEntitySettings(doc, { type: "ENTITY_SETTINGS", id });

    if (!targetedSettingsSet) {
      continue;
    }

    if (settingsType) {
      const settings = docGetSettingSetSettings(doc, { type: "SETTINGS_SET", id: targetedSettingsSet.id });

      if (!settings.some(({ type }) => type === settingsType)) {
        continue;
      }
    }

    if (uniqueSettingsOrSettingsSetIds.has(targetedSettingsSet.id)) {
      continue;
    }

    entitySettings[id] = entitySetting;
    uniqueSettingsOrSettingsSetIds.add(targetedSettingsSet.id);
  }

  return sortTableBySortIndex(entitySettings);
}

/**
 * Get the eligible targeted settings sets for a given set of handles
 */
export function docGetEligibleTargetedSettingsSetsForEntities(
  doc: Doc,
  entityIds: string | string[],
  settingsType?: SettingsType,
): Table<SettingsSet | Group> {
  const handles = docNormalizeEntityIdToHandles(doc, entityIds, entityTypesEligibleForEntitySettings, (entityHandle) =>
    isEntitySettingsEligibleHandle(doc, entityHandle),
  );

  let eligibleSettingsTypes = getEligibleSettingsTypesForEntities(handles);

  if (settingsType) {
    if (!eligibleSettingsTypes.has(settingsType)) {
      return {};
    }

    eligibleSettingsTypes = new Set([settingsType]);
  }

  const eligibleTargetedSettingsSets: RWTable<SettingsSet | Group> = {};

  const groupedAndUngroupedEntities = docGetGroupedAndUngroupedEntities(
    doc,
    "SETTINGS_SET",
    entityIsTargetedSettingsSet,
  );

  const targetedSettingsSetGroups = groupedAndUngroupedEntities?.settingsSets?.targetedSettingsSets ?? [];

  const checkSettingsSetValidity = (handle: SettingsSetHandle) => {
    const settings = docGetSettingSetSettings(doc, handle);

    if (!settings.length) {
      return false;
    }

    const filteredSettings = settings.filter(({ type }) => eligibleSettingsTypes.has(type));

    if (!filteredSettings.length) {
      return false;
    }

    return true;
  };

  for (const entity of targetedSettingsSetGroups) {
    if (entity.entityType === "SETTINGS_SET") {
      if (!entityIsTargetedSettingsSet(entity)) {
        continue;
      }

      if (!checkSettingsSetValidity({ type: "SETTINGS_SET", id: entity.id })) {
        continue;
      }

      eligibleTargetedSettingsSets[entity.id] = entity;
    } else {
      const groupSettingsSets = docEntityGetChildren(doc, { type: "GROUP", id: entity.id }) as SettingsSetHandle[];
      const validGroupedSettingsSets = groupSettingsSets.filter((settingsSet) => checkSettingsSetValidity(settingsSet));

      if (!validGroupedSettingsSets.length) {
        continue;
      }

      eligibleTargetedSettingsSets[entity.id] = entity;
    }
  }

  return eligibleTargetedSettingsSets;
}

/**
 * Get the Settings Set associated w/ a given Entity Setting's settingsOrSettingsSetId
 */
export function docGetSettingsSetForEntitySettings(doc: Doc, handle: EntitySettingsHandle): SettingsSet | undefined {
  const entitySetting = docGetTableEntity(doc, handle);

  if (!entitySetting) {
    return undefined;
  }

  const id = entitySetting.settingsOrSettingsSetId;
  const settingsSet = doc.settingsSets[id];

  if (entityIsTargetedSettingsSet(settingsSet)) {
    return settingsSet;
  }

  const settings = doc.settings[entitySetting.settingsOrSettingsSetId];

  if (!settings?.parentId) {
    return undefined;
  }

  return doc.settingsSets[settings.parentId];
}

/**
 * Get the eligible settings types for a given set of handles - defined in {@link docEntityTypeToEligibleTargetedSettingsType}
 */
export function getEligibleSettingsTypesForEntities(handles: EntitySettingsEligibleHandle[]) {
  return handles.reduce((settingsTypes, handle) => {
    const eligibleTypes = docEntityTypeToEligibleTargetedSettingsType[handle.type];

    for (const eligibleType of eligibleTypes) {
      settingsTypes.add(eligibleType);
    }

    return settingsTypes;
  }, new Set<SettingsType>());
}

/**
 * Get the Entity associated w/ a given Entity Setting's entityId
 */
export function docGetEntityForEntitySetting(
  doc: Doc,
  handle: EntitySettingsHandle,
): EligibleEntitySettingEntity | undefined {
  const entitySetting = doc.entitySettings[handle.id];

  if (!entitySetting) {
    return undefined;
  }

  const entityHandle = docGetEntityHandleById(doc, entitySetting.entityId, entityTypesEligibleForEntitySettings);
  return entityHandle ? (docGetEntity(doc, entityHandle) as EligibleEntitySettingEntity) : undefined;
}

export function docGetTargetedSettingsSetsForEntity(doc: Doc, handle: DocEntityHandle): Table<SettingsSet> | undefined {
  if (!isEntitySettingsEligibleHandle(doc, handle)) {
    return undefined;
  }

  const entitySettings = docGetEntitySettingsForEntity(doc, handle);

  if (!entitySettings.length) {
    return undefined;
  }

  const targetedSettingsSets: RWTable<SettingsSet> = {};

  const entitySettingsBySettingsOrSettingsSetId = arrayUniqueByKey(entitySettings, "settingsOrSettingsSetId");

  for (const entitySetting of entitySettingsBySettingsOrSettingsSetId) {
    const settingsSet = docGetSettingsSetForEntitySettings(doc, { type: "ENTITY_SETTINGS", id: entitySetting.id });

    if (!settingsSet) {
      continue;
    }

    targetedSettingsSets[settingsSet.id] = settingsSet;
  }

  return targetedSettingsSets;
}

/**
 * Get Settings Set associated w/ a given Entity Setting's settingsOrSettingsSetId
 */
export function docGetEntitySettingsForSettingsSet(
  doc: Doc,
  handle: SettingsSetHandle,
  entityIds?: string | string[],
): Table<EntitySettings> | undefined {
  const settingsSet = doc.settingsSets[handle.id];
  const handles = entityIds
    ? docNormalizeEntityIdToHandles(doc, entityIds, entityTypesEligibleForEntitySettings, (entityHandle) =>
        isEntitySettingsEligibleHandle(doc, entityHandle),
      )
    : [];

  if (settingsSet?.type !== SettingsSetBehaviorType.TARGETED) {
    return undefined;
  }

  const entitySettings: RWTable<EntitySettings> = {};

  for (const id in doc.entitySettings) {
    const entitySetting = doc.entitySettings[id];
    if (handles.length && !handles.some((handle) => handle.id === entitySetting.entityId)) {
      continue;
    }

    const setting = doc.settings[entitySetting.settingsOrSettingsSetId];

    if (setting && setting.parentId === settingsSet.id) {
      entitySettings[id] = entitySetting;
      continue;
    }

    if (entitySetting.settingsOrSettingsSetId === settingsSet.id) {
      entitySettings[id] = entitySetting;
    }
  }

  return entitySettings;
}

/**
 * Get the Entity Settings for a given Entity from the Entity Settings table on the doc
 */
export function docGetEntitySettingsForEntity(doc: Doc, handle: EntitySettingsEligibleHandle) {
  const entityId = docGetEntityId(doc, handle);

  if (!entityId) {
    return [];
  }

  const entitySettings = objectFilter(doc.entitySettings, (entitySetting) => entitySetting.entityId === entityId);
  return sortTableBySortIndex(entitySettings);
}

export function docGetEntityForSettingsOrSettingsSet(
  doc: Doc,
  settingsOrSettingsSetId: string,
): EligibleEntitySettingEntity | undefined {
  if (objectIsEmpty(doc.entitySettings)) {
    return undefined;
  }

  // Find the entitySettings entry that matches the provided settingsOrSettingsSetId
  const matchingEntitySetting = Object.values(doc.entitySettings).find(
    (entitySetting) => entitySetting.settingsOrSettingsSetId === settingsOrSettingsSetId,
  );

  if (!matchingEntitySetting) {
    return undefined;
  }

  // Get the entity handle and then the entity itself
  const entityHandle = docGetEntityHandleById(
    doc,
    matchingEntitySetting.entityId,
    entityTypesEligibleForEntitySettings,
  );
  return entityHandle ? (docGetEntity(doc, entityHandle) as EligibleEntitySettingEntity) : undefined;
}
